import Link from 'next/link';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { IMAGINE_LINK } from '@web/features/const';
import { Button } from '@web/ui/components';
import { SharpImage } from '@web/ui/components/SharpImage';
import { TileHyperLinkIcon } from '@web/ui/icons';

import { ImagineIcon } from '../brand-icons';
import { HubTile } from '../HubTile';

import ImagineIllustration from './imagine-illustration.png';

const SHubTile = styled(HubTile)`
  padding-bottom: 0;
`;

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
  ${({ theme }) => theme.breakpoints.down('lg')} {
    flex-direction: column;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  color: ${({ theme }) => theme.color.onSurface};
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  gap: ${({ theme }) => theme.spacing('2000')};
  ${({ theme }) => theme.breakpoints.down('lg')} {
    max-width: none;
    width: 100%;
    padding-block: ${({ theme }) => theme.spacing('3500')};
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('0750')};
`;

const Title = styled.h3`
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['4']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  color: ${({ theme }) => theme.color.onSurface};
  text-align: center;
`;

const Description = styled.p`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  color: ${({ theme }) => theme.color.onSurface};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  text-align: center;
  white-space: pre;
`;

const VisualContainer = styled.div`
  position: relative;
  height: 100%;
  min-height: 22.5rem;
  display: flex;
  align-self: stretch;
  flex: 1 0 0;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const Visual = styled(SharpImage)`
  position: absolute;
  left: -2.5rem;
  object-fit: cover;
  width: auto;
  min-width: 100%;
  height: 100%;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    position: static;
  }
`;

const SButton = styled(Button)`
  background-color: ${({ theme }) => theme.color.orange};
`;

export const ImagineTile = () => {
  const t = useTranslations();

  return (
    <Link href={IMAGINE_LINK} target="_blank" rel="noreferrer">
      <SHubTile
        textColor="onSurface"
        background="surfaceContainerLow"
        title={t('IMAGINE_HUB_TILE_TITLE')}
        badge={<TileHyperLinkIcon size="0750" />}
        stateLayer="onSurface"
      >
        <ContentContainer>
          <LeftContainer>
            <ImagineIcon size="3000" />
            <TextContent>
              <Title>{t('IMAGINE_HUB_TILE_SUBTITLE')}</Title>
              <Description>{t('IMAGINE_HUB_TILE_DESCRIPTION')}</Description>
            </TextContent>

            <Link href={IMAGINE_LINK} target="_blank" rel="noreferrer">
              <SButton label={t('IMAGINE_HUB_TILE_BUTTON')} />
            </Link>
          </LeftContainer>

          <VisualContainer>
            <Visual src={ImagineIllustration} />
          </VisualContainer>
        </ContentContainer>
      </SHubTile>
    </Link>
  );
};
